import { useCallback, useEffect, useRef, useState } from "react";
import _ from "lodash";
import { useDispatch, useSelector, useStore } from "react-redux";
import { useScrollTrigger } from "@mui/material";
import { useSearchParams } from "react-router-dom";

import { addAllBoard, searchCard, setDateRange } from "../../../redux/SearchSlice";
import { setAllGroupUser, setGroupUser } from "../../../redux/TaskGroupingSlice";
import { useResponsive } from "../../../hooks/useResponsive";
import { useTeams } from "../../../reactQuery/teams";
import { useCustomSearchParams } from "../../../hooks/useCustomSearchParams";
import { useUsers } from "../../../reactQuery/users";
import { useSetMultipleParams } from "../../../hooks/useSetMultipleParams";
import {
  fixedDigitsAfterDecimal,
} from "../../../utils";
import {
  getFlattenedGroupingUsers,
  parseWorklogDateRange,
} from "../utils/myTaskUtils";

export function useWorklogHeaderState() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchParam, setSearchParam] = useCustomSearchParams("txt");
  const [userParams] = useCustomSearchParams("uid");
  const dispatch = useDispatch();
  const store = useStore();
  const trigger = useScrollTrigger();
  const isMobile = useResponsive("down", "md");
  const workHours = useSelector((state) => state.search.totalWorkHours);
  const groupingUser = useSelector(
    (state) => state.groupingUser.allocationUsers
  );
  const [setMultipleSearchParams] = useSetMultipleParams();
  const { teams } = useTeams();
  const { users } = useUsers();

  const [filterDrawer, setFilterDrawer] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [openSearchField, setOpenSearchField] = useState(false);
  const syncedUserParamRef = useRef(null);

  const oldDate = searchParams.get("date");

  useEffect(() => {
    const availableUsers = users?.data?.data?.responseData || [];

    if (!userParams || !availableUsers.length) {
      syncedUserParamRef.current = null;
      return;
    }

    if (syncedUserParamRef.current === userParams) {
      return;
    }

    userParams.split(",").forEach((userId) => {
      const matchedUser = availableUsers.find(
        (user) => user.user_id === Number(userId)
      );

      if (matchedUser) {
        dispatch(setGroupUser(matchedUser));
      }
    });

    syncedUserParamRef.current = userParams;
  }, [dispatch, userParams, users]);

  useEffect(() => {
    setInputValue(searchParam || "");
    dispatch(searchCard(searchParam || ""));
  }, [dispatch, searchParam]);

  useEffect(() => {
    if (oldDate && isMobile) {
      dispatch(setDateRange(parseWorklogDateRange(oldDate)));
    }
  }, [dispatch, isMobile, oldDate]);

  const removeSearchParams = useCallback(
    (paramsToRemove) => {
      const nextSearchParams = new URLSearchParams(searchParams);
      paramsToRemove.forEach((param) => {
        nextSearchParams.delete(param);
      });
      setSearchParams(nextSearchParams);
    },
    [searchParams, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    dispatch(addAllBoard([]));
    dispatch(setAllGroupUser([]));
    dispatch(setDateRange(null));
    removeSearchParams(["date", "board", "uid"]);
  }, [dispatch, removeSearchParams]);

  const debouncedSearch = useRef(
    _.debounce((value) => {
      dispatch(searchCard(value));
      setSearchParam("txt", value);
    }, 300)
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleApplyWorklogFilter = useCallback(() => {
    const currentState = store.getState();
    const currentBoardSearch = currentState.search.boardSearch;
    const currentGroupingUser = currentState.groupingUser.allocationUsers;

    setMultipleSearchParams([
      {
        name: "board",
        value: currentBoardSearch.join(","),
      },
      {
        name: "uid",
        value: (currentGroupingUser[0]?.users || [])
          .map((item) => item.user_id)
          .join(","),
      },
    ]);
    setFilterDrawer(false);
  }, [setMultipleSearchParams, store]);

  const handleOpenSearchField = useCallback(() => {
    setOpenSearchField((currentValue) => !currentValue);
  }, []);

  const handleChange = useCallback(
    (event) => {
      const value = event.target.value.trimStart();
      setInputValue(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  return {
    clearFilters,
    filterDrawer,
    groupingUser,
    handleApplyWorklogFilter,
    handleChange,
    handleOpenSearchField,
    hoursLabel: `${fixedDigitsAfterDecimal(workHours)} hours`,
    inputValue,
    isMobile,
    openSearchField,
    openFilterDrawer: () => setFilterDrawer(true),
    closeFilterDrawer: () => setFilterDrawer(false),
    teams,
    trigger,
    visibleGroupingUsers: getFlattenedGroupingUsers(groupingUser),
  };
}
